import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { ReviewListItemProps } from './review-list/review-list-item';

/** Passed to `onAcceptAndPublish` from the split Accept dropdown (public vs internal). */
export type AcceptAndPublishVisibility = 'public' | 'internal';

export type LoadedDiffDoc = {
  isNew: boolean;
  diffTitle: string;
  diffContent: React.ReactNode;
};

type ReviewsSelectionValue = {
  selectedItem: ReviewListItemProps | null;
  setSelectedItem: (item: ReviewListItemProps | null) => void;
  loadedDiffDoc: LoadedDiffDoc | null;
  setLoadedDiffDoc: (doc: LoadedDiffDoc | null) => void;
  loadingDiff: boolean;
  setLoadingDiff: (loading: boolean) => void;
  onApproveAllSuggestions: () => void | Promise<void>;
  onRejectAllSuggestions: () => void | Promise<void>;
  onAcceptAndPublish?: (visibility: AcceptAndPublishVisibility) => void | Promise<void>;
  onApplyAllInEditor?: () => Promise<void> | void;
  onRejectAllInEditor?: () => Promise<void> | void;
  onOpenArticleLink?: () => void;
  onSourceLinkClick?: () => void;
  breadcrumb?: string;
  suggestionReasonBody?: string;
  /** When true, omit the "publish publicly" item from the Accept & publish menu. */
  hideAcceptAndPublishPublic?: boolean;
};

const ReviewsContext = createContext<ReviewsSelectionValue | null>(null);

export type ReviewsProviderProps = {
  children: React.ReactNode;
  /** Optional initial selection (e.g. from server or mock data) */
  defaultSelectedItem?: ReviewListItemProps | null;
  onApproveAllSuggestions: () => void | Promise<void>;
  onRejectAllSuggestions: () => void | Promise<void>;
  onAcceptAndPublish?: (visibility: AcceptAndPublishVisibility) => void | Promise<void>;
  onApplyAllInEditor?: () => Promise<void> | void;
  onRejectAllInEditor?: () => Promise<void> | void;
  onOpenArticleLink?: () => void;
  onSourceLinkClick?: () => void;
  breadcrumb?: string;
  suggestionReasonBody?: string;
  hideAcceptAndPublishPublic?: boolean;
};

export function ReviewsSelectionProvider({
  children,
  defaultSelectedItem = null,
  onApproveAllSuggestions,
  onRejectAllSuggestions,
  onAcceptAndPublish,
  onApplyAllInEditor,
  onRejectAllInEditor,
  onOpenArticleLink,
  onSourceLinkClick,
  breadcrumb,
  suggestionReasonBody,
  hideAcceptAndPublishPublic = false,
}: ReviewsProviderProps) {
  const [userSelectedItem, setUserSelectedItem] = useState<ReviewListItemProps | null>(null);
  const selectedItem = userSelectedItem ?? defaultSelectedItem;

  const [loadedDiffDoc, setLoadedDiffDoc] = useState<LoadedDiffDoc | null>(null);

  const [loadingDiff, setLoadingDiff] = useState(false);

  const handleSelect = useCallback((item: ReviewListItemProps | null) => setUserSelectedItem(item), []);
  const value = useMemo<ReviewsSelectionValue>(
    () => ({
      selectedItem,
      setSelectedItem: handleSelect,
      loadedDiffDoc,
      setLoadedDiffDoc,
      loadingDiff,
      setLoadingDiff,
      onApproveAllSuggestions,
      onRejectAllSuggestions,
      onAcceptAndPublish,
      onApplyAllInEditor,
      onRejectAllInEditor,
      onOpenArticleLink,
      onSourceLinkClick,
      breadcrumb,
      suggestionReasonBody,
      hideAcceptAndPublishPublic,
    }),
    [
      selectedItem,
      handleSelect,
      loadedDiffDoc,
      loadingDiff,
      onApproveAllSuggestions,
      onRejectAllSuggestions,
      onAcceptAndPublish,
      onApplyAllInEditor,
      onRejectAllInEditor,
      onOpenArticleLink,
      onSourceLinkClick,
      breadcrumb,
      suggestionReasonBody,
      hideAcceptAndPublishPublic,
    ],
  );

  return <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>;
}

export function useReviewsSelection(): ReviewsSelectionValue {
  const ctx = useContext(ReviewsContext);
  if (!ctx) {
    throw new Error('useReviewsSelection must be used within ReviewsSelectionProvider');
  }

  return ctx;
}
